const ensureItems = (connection) => {
  if (!connection?.items) return [];
  return connection.items.filter(Boolean);
};

const resolveInventoryProductName = (inventory = {}) => {
  if (inventory.productName) return inventory.productName;
  const fromLocation = (inventory.inventoryItems?.items || [])
    .map((item) => item?.location?.productName)
    .find(Boolean);
  if (fromLocation) return fromLocation;
  return inventory.productCode || "";
};

const normalizeJoinedInbound = (workOrder = {}) => {
  return (
    workOrder?.inputInventory?.inboundShipment ||
    workOrder?.inputInventory?.inbound ||
    null
  );
};

// Defensive mapping for any legacy inbound-style state values that may exist on
// WorkOrder rows. Canonical writers (managerApp/resources/workOrders/WorkOrderCreate.js
// and crewApp/pages/workOrder/PlacedShipmentCard.js) only persist values from
// `WorkOrderState`. Audit any new writer that creates WorkOrder rows to ensure
// it does the same; otherwise drop this mapping and treat the input as canonical.
const normalizeWorkOrderState = (state) => {
  switch (state) {
    case "PROCESS_SHIPMENT":
    case "APPROVE_SHIPMENT":
      return "READY";
    case "PROCESS_INVENTORY":
      return "IN_PROGRESS";
    case "PROCESS_INVENTORY_REVIEW":
    case "APPROVE_INVENTORY":
    case "APPROVE_INVENTORY_REVIEW":
      return "PENDING_REVIEW";
    case "COMPLETED":
      return "COMPLETED";
    default:
      return state || "READY";
  }
};

export const buildWorkOrderInventories = (workOrder = {}) => {
  const inputInventory = workOrder?.inputInventory;
  const outputInventories = ensureItems(workOrder?.outputInventories);

  const allInventories = outputInventories.filter(Boolean).map((inventory) => ({
    ...inventory,
    productName: resolveInventoryProductName(inventory),
    workOrderId: inventory.workOrderId || workOrder.id || "",
    inboundShipmentId:
      inventory.inboundShipmentId || inputInventory?.inboundShipmentId || "",
  }));

  return { items: allInventories };
};

export const adaptWorkOrderForCrewList = (workOrder = {}) => {
  const inbound = normalizeJoinedInbound(workOrder);

  return {
    ...workOrder,
    state: normalizeWorkOrderState(workOrder?.state),
    placedDateTime: inbound?.placedDateTime || workOrder.createDateTime || "",
    lotNumber: workOrder?.inputInventory?.lotNumber || "",
    // carrierType is RAIL/TRUCK from the inbound; workType ("INBOUND"/…) is a
    // different classifier and would mislead the carrier icon/label.
    carrierType: inbound?.carrierType || "",
    unloadedBy: inbound?.unloadedBy || "",
    sit: Boolean(inbound?.sit),
    inboundShipmentId:
      workOrder?.inputInventory?.inboundShipmentId || inbound?.id || "",
  };
};

export const adaptWorkOrderForCrewDetail = (workOrder = {}) => {
  if (!workOrder?.id) {
    throw new Error("Work order payload is missing id");
  }

  const inbound = normalizeJoinedInbound(workOrder);
  const inventories = buildWorkOrderInventories(workOrder);

  return {
    ...workOrder,
    state: normalizeWorkOrderState(workOrder?.state),
    inventories,
    placedDateTime: inbound?.placedDateTime || workOrder.createDateTime || "",
    // See list adapter — do not fall back to WorkOrder.workType for carrierType.
    carrierType: inbound?.carrierType || "",
    lotNumber: workOrder?.inputInventory?.lotNumber || "",
    inboundShipmentId:
      workOrder?.inputInventory?.inboundShipmentId || inbound?.id || "",
    unloadedBy: inbound?.unloadedBy || "",
    reviewedBy: inbound?.reviewedBy || "",
    secondaryReview:
      typeof inbound?.secondaryReview === "boolean"
        ? inbound.secondaryReview
        : Boolean(workOrder?.secondaryReview),
    sit: Boolean(inbound?.sit),
    stockImport: Boolean(inbound?.stockImport || workOrder?.stockImport),
    containerDrop:
      typeof inbound?.containerDrop === "boolean"
        ? inbound.containerDrop
        : Boolean(workOrder?.containerDrop),
    containerNumber: inbound?.containerNumber || "",
    unsafeShipment:
      typeof inbound?.unsafeShipment === "boolean"
        ? inbound.unsafeShipment
        : Boolean(workOrder?.unsafeShipment),
    unsafeShipmentNote: inbound?.unsafeShipmentNote || "",
    brokenSeal:
      typeof workOrder?.brokenSeal === "boolean"
        ? workOrder.brokenSeal
        : Boolean(inbound?.brokenSeal),
    brokenSealNote: workOrder?.brokenSealNote || inbound?.brokenSealNote || "",
    otherException:
      typeof workOrder?.otherException === "boolean"
        ? workOrder.otherException
        : Boolean(inbound?.otherException),
    otherExceptionNote:
      workOrder?.otherExceptionNote || inbound?.otherExceptionNote || "",
    sealNumbers: workOrder?.sealNumbers || inbound?.sealNumbers || [],
    seals: workOrder?.seals || inbound?.seals || { items: [] },
    checklist: workOrder?.checklist || inbound?.checklist || [],
    workOrderItems: workOrder?.workOrderItems || { items: [] },
    inputInventory: workOrder?.inputInventory || null,
    // The railcar belongs to the joined inbound, never to the WorkOrder row.
    // Crew screens gate track placement on it, so an unmapped trackCar reads as
    // "not spotted" and disables storing for a car that is spotted.
    trackCar: workOrder?.trackCar || inbound?.trackCar || null,
  };
};
